"use client";
import Link from "next/link";
import parse from "html-react-parser";
import { cn } from "@/lib/utils";
import Text from "@/components/static/Text";
import Tags from "@/components/static/Tags";
import Tag from "@/components/static/Tag";
import Button from "@/components/static/Button";
import Headline from "@/components/static/Headline";
import Image from "next/image";
import IconArrow from "@/icons/ArrowRight";
import {
    SectionProps,
    HeadlineProps,
    ProductProps,
    ImageProps,
    ButtonProps,
    TermProps,
} from "@/lib/types";
import useSWR from "swr";
import placeholder from "../../../../public/images/product-placeholder.svg";

const fetcher = (url: string) =>
    fetch(`${process.env.NEXT_PUBLIC_API_URL}${url}`).then((res) => res.json());

export default function Product({ post, className }: { post: ProductProps; className?: string }) {
    const { data: productCatSecondData } = useSWR<TermProps[]>(
        `wp/v2/product_cat_second?include=${post.product_cat_second.join(",")}&acf_format=standard`,
        fetcher
    );

    const { data: productCatThirdData } = useSWR<TermProps[]>(
        `wp/v2/product_cat_third?include=${post.product_cat_third.join(",")}&acf_format=standard`,
        fetcher
    );

    return (
        <Link
            href={post.link ?? ""}
            className={cn(
                "group block relative bg-bgElement overflow-hidden rounded-3xl h-full",
                className
            )}
        >
            <div className='justify-between .group-hover:scale-[98%] transition-transform'>
                <div className='p-10 overflow-hidden relative h-[275px] w-full bg-[#DFE5EB]'>
                    <Image
                        src={post.acf.imageHero.url ?? placeholder}
                        height={post.acf.imageHero.height ?? 100}
                        width={post.acf.imageHero.width ?? 100}
                        alt={post.acf.imageHero.alt ?? "Placeholder"}
                        className='size-full object-contain object-center'
                    />
                    {post.product_cat_second.length > 0 && (
                        <Tags className='absolute top-4 left-4'>
                            {productCatSecondData?.map((item: TermProps, key: number) => (
                                <Tag key={key} label={item.name} />
                            ))}
                        </Tags>
                    )}

                    {post.product_cat_third.length > 0 && (
                        <div className='absolute right-4 bottom-4 flex flex-wrap w-full max-w-[260px] justify-end gap-1'>
                            {productCatThirdData?.map((item, key) => (
                                <Image
                                    key={key}
                                    src={item.acf.icon.url ?? "https://placehold.co/38x38"}
                                    height={item.acf.icon.height ?? 38}
                                    width={item.acf.icon.width ?? 38}
                                    alt={item.acf.icon.alt ?? "Icon"}
                                    aria-label={item.name}
                                    title={item.name}
                                    className='size-[38px]'
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div className='px-8 py-6'>
                    <Headline
                        className='leading-[120%]'
                        toplineClassName={"transition"}
                        wrapperClassName='!m-0'
                        tag='h4'
                    >
                        {post.title.rendered ?? ""}
                    </Headline>
                    <div className='text-textSecondary text-[12px] mt-1 mb-6'>
                        Katalognummer: {post.acf.vat}
                    </div>
                    {post.acf.text && (
                        <Text className='prose mt-4 transition text-textSecondary prose-ul:list-disc prose-ul:gap-2 prose-ul:leading-tight prose-ul:p-0 prose-ul:m-4 prose-li:p-0 prose-li:m-0'>
                            {parse(post.acf.text)}
                        </Text>
                    )}
                </div>
            </div>
            {/* {post._embedded?.["wp:featuredmedia"] && (
                <div className='absolute flex items-center justify-center w-full h-full inset-0 p-20'>
                    <Image
                        src={post._embedded["wp:featuredmedia"][0].source_url ?? ""}
                        alt={post._embedded["wp:featuredmedia"][0].alt_text ?? ""}
                        width={post._embedded["wp:featuredmedia"][0].media_details.width ?? ""}
                        height={post._embedded["wp:featuredmedia"][0].media_details.height ?? ""}
                        className='relative w-full h-full max-h-[60%] aspect-video object-contain object-center -top-[10%]'
                    />
                </div>
            )} */}
        </Link>
    );
}
